export default {
    shared: {
        newWebsite: 'New website',
        underConstruction: 'Under construction',
        noIndex: 'No index',
        validationFailed: 'Validation failed.',
        showOutput: 'Show output',
        hideOutput: 'Hide output',
        // Compact "how long ago", for job lists (backups, theme deployments).
        age: {
            seconds: '{n}s ago',
            minutes: '{n}m ago',
            hours: '{n}h ago',
            days: '{n}d ago',
        },
    },
    list: {
        title: 'Websites',
        stats: {
            websites: 'Websites',
            inactiveCount: '{n} inactive',
        },
        searchPlaceholder: 'Search by code or name…',
        columns: {
            locales: 'Locales',
            storage: 'Storage',
        },
        noIndexTitle: 'Sends noindex on every page and disallows everything in robots.txt',
        underConstructionTitle: 'Visitors get a coming-soon page and a 503; only allowed addresses see the site',
        editing: 'Editing',
        switchTo: 'Switch to',
        deleted: 'Deleted',
        deletedTitle: 'Offline, removed for good on {date}',
        undoDelete: 'Undo delete',
        noMatches: 'No websites match your search.',
        empty: 'No websites yet.',
    },
    edit: {
        tabs: {
            general: 'General',
            contact: 'Contact',
            theme: 'Theme',
            hosts: 'Hosts',
            forms: 'Forms',
            moduleSettings: 'Module settings',
            modules: 'Modules',
            backups: 'Backups',
        },
        banners: {
            deleted: 'Deleted. The site is offline and is removed for good on {date}, with its files and backups.',
            underConstruction: 'Under construction. Visitors get a “coming soon” page and a {status}; only the allowed addresses see the site. Turn this off in General → Under construction when it goes live.',
            noindex: 'Hidden from search engines. Every page sends {noindex} and robots.txt disallows everything. Turn this off in General → Search engines when the site goes live.',
        },
        toasts: {
            created: 'Website created',
            hostNotAdded: 'Website created, but the host was not added: {reason}',
            hostValidationFailed: 'validation failed',
            syncQueued: 'Sync queued. Reviews will appear within a few minutes.',
        },
        confirmDelete: 'Delete website "{name}"? It goes offline now and is removed for good in 30 days, with its pages, orders, media, files and backups. Until then a super admin can undo this.',
        identity: {
            title: 'Identity',
            codeHintNew: 'Derived from the name until you type your own. Lowercase letters, digits, dashes (2–64).',
            codeHint: 'Lowercase letters, digits, dashes (2–64)',
        },
        firstHost: {
            title: 'First host',
            hint: 'Follows the code as {example} until you type your own. Added as the canonical host; more can be added on the Hosts tab afterwards. Leave empty to add none.',
        },
        searchEngines: {
            title: 'Search engines',
            hide: 'Hide this site from search engines',
            hideHint: 'For sites still being built. Every page sends noindex/nofollow, and robots.txt disallows everything. Turn it off when the site goes live.',
            newNote: 'On by default for a new site. A site that gets crawled while half-finished leaves placeholder pages in Google long after launch.',
            onNote: 'Removing pages already indexed is up to the search engine and takes a while, so switch this on before the site is reachable, not after.',
        },
        construction: {
            hold: 'Hold this site behind a placeholder',
            holdHint: 'Everyone except the addresses below sees a “coming soon” page instead of the site, and gets a 503 so no search engine keeps it. The editor keeps working.',
            newNote: 'On by default for a new site. A site is reachable the moment its hostnames exist, which is long before it is ready to be read.',
            allowedTitle: 'Addresses that see the real site',
            removeIp: 'Remove {ip}',
            noneListed: 'None for this site. Our offices and the ZenRise crawler are allowed for every site centrally, so this list is only for extra addresses.',
            addAddress: 'Add an address',
            addPlaceholder: 'e.g. 203.0.113.7 or 203.0.113.0/24',
            addHint: 'One address, or a range in CIDR notation.',
            alreadyListed: '{ip} is already on the list.',
            connectingFrom: 'You are connecting from {ip}.',
            addIt: 'Add it',
            ownListed: 'Your own address {ip} is on the list.',
            lockout: 'Your own address is not on this list. Unless a range above covers it, or you are allowed centrally, you will get the placeholder too once you save.',
        },
        locales: {
            title: 'Locales',
            defaultLocale: 'Default locale',
            locales: 'Locales',
            hint: 'Comma-separated, e.g. en, nl, de',
        },
        mail: {
            title: 'Mail sender',
            intro: 'Default From: address used on every outgoing email (form submissions, customer auth, order receipts). Required: without it, mail is not sent. Make sure the domain has SPF and DKIM set up to authorise our SMTP relay. Per-channel and per-form overrides live on the {moduleSettings} + {forms} tabs.',
            fromEmail: 'From email',
            fromName: 'From name (optional)',
            fromNamePlaceholder: 'Defaults to website name',
        },
        cookies: {
            title: 'Cookies (AVG / GDPR)',
            intro: 'Show the cookie consent banner to visitors. Functional cookies (session, login, cart, CSRF) are always on and never require consent.',
            show: 'Show cookie banner',
            policyUrl: 'Cookie policy URL',
            policyUrlPlaceholder: '/cookies or https://…',
            policyUrlHint: "Linked from the banner's 'Read more' section.",
            readMore: 'Read-more text',
            readMoreHint: "Plain text shown when the visitor clicks 'Read more'. Leave blank to use the default copy.",
            version: 'Policy version',
            versionNote: 'Current: {version}. Bump to re-prompt every visitor (existing consent cookies become invalid).',
            bump: 'Bump version',
        },
        reviews: {
            title: 'Google reviews',
            intro: "Pull this site's Google Business reviews into the public widget. A daily worker keeps reviews up to date; reviewer profile photos are downloaded so the widget stays self-hosted. The Place id is verified against Google when you save.",
            placeId: 'Google Place id',
            placeIdHint: "Found via Google's Place id finder. Leave empty to disable.",
            sync: 'Sync',
            syncNote: 'Runs daily at 04:00 UTC. Trigger now to refresh after configuring.',
            queuing: 'Queuing…',
            syncNow: 'Sync now',
        },
        theme: {
            title: 'Theme',
            hintNew: 'Follows the code until you type your own. Matches the directory name on NFS.',
            hint: 'Theme code; matches the directory name on NFS.',
            repository: 'Theme repository',
            repositoryHintNew: 'Follows the code as {example} until you type your own.',
            repositoryHint: 'Gitea path {path} (e.g. {example}), or the HTTPS clone URL of a repository on another git host (e.g. {url}).',
            repositoryToken: 'Repository access token',
            repositoryTokenHint: 'For a private repository: the token, or user:token for a host that wants a user name with it (GitLab oauth2:<token>, Bitbucket x-token-auth:<token>). Changing the repository removes the stored token unless you enter a new one.',
            removeRepositoryToken: 'Remove the stored token',
            superOnly: 'Only a super admin can change the theme or its repository.',
            activeVersion: 'Active version: {version}',
        },
        hosts: {
            title: 'Hosts',
            hostname: 'Hostname',
            canonical: 'Canonical',
            redirectsTo: 'Redirects to',
            makeCanonical: 'Make canonical',
            nowhere: '(nowhere)',
            samePath: 'same path',
            empty: 'No hosts yet.',
            addHostname: 'Add hostname',
            redirectOptional: 'Redirects to (optional)',
            redirectHint: 'A vanity domain can name the one page it stands for: a visit to its homepage 301s there instead of to ours. Empty keeps the path the visitor asked for.',
            canonicalNote: 'The canonical host is the one everything else redirects to, so it cannot itself redirect anywhere.',
            confirmRemove: 'Remove this host?',
        },
    },
    company: {
        sections: {
            contact: 'Contact & address',
            company: 'Company',
            social: 'Social media',
            whatsapp: 'WhatsApp button',
        },
        lookup: {
            fillFrom: 'Fill in from',
            buttons: {
                google: 'Google',
                kvk: 'KvK',
                website: 'Website',
            },
            kvkUnavailable: 'Needs a KvK API key (KVK_API_KEY) on this install.',
            sources: {
                google: 'Fill in from Google',
                kvk: 'Fill in from the KvK register',
                website: 'Fill in from the website',
            },
            query: {
                google: 'Business name and place',
                kvk: 'KvK number or business name',
            },
            queryHint: {
                google: 'As you would search for it in Google Maps.',
                kvk: 'Add ", place" to a name to search one place only, e.g. Bakkerij Jansen, Breda.',
            },
            url: 'Website address',
            urlHint: 'Usually the homepage: its footer links to the social profiles. One page is read.',
            read: 'Read',
            noResults: 'Nothing found. Try fewer words, or another spelling.',
            notFound: 'This result is no longer there. Search again.',
            failed: 'The lookup failed: {reason}',
            nothingFound: 'Nothing to take over was found.',
            nothingNew: 'Everything found is already filled in.',
            reviewIntro: 'Tick what to take over. Fields that are already filled in are not ticked, so nothing you typed is replaced unless you choose to.',
            field: 'Field',
            found: 'Found',
            now: 'Now',
            empty: 'empty',
            apply: 'Take over | Take over 1 value | Take over {n} values',
            applyAndScan: 'Take over, then read the website',
            applied: 'Nothing taken over. | 1 value taken over. Save to keep it. | {n} values taken over. Save to keep them.',
            kvkKinds: {
                hoofdvestiging: 'Main branch',
                nevenvestiging: 'Branch',
                rechtspersoon: 'Legal entity',
            },
        },
        contact: {
            title: 'Contact',
            intro: "Used by the theme for the header, the footer and the contact page, and by the site's structured data. Themes read these with {fn}. Leave a field blank and the theme skips it.",
            phoneHint: 'Shown as typed. The tel: link is built from it.',
            emailHint: 'The public address, not the one mail is sent from.',
            whatsappHint: 'Include the country code: wa.me links need it.',
        },
        address: {
            title: 'Address',
            street: 'Street and number',
            postalCode: 'Postal code',
            city: 'City',
            countryHint: 'Two-letter code. Street and city are both needed before the address is published in structured data.',
        },
        registration: {
            title: 'Registration',
            kvk: 'KvK number',
            vat: 'VAT number',
        },
        business: {
            title: 'Business for search engines',
            intro: 'Published only as structured data, for local search and the map results. Leave the type empty for an organisation without a place customers visit or call out from.',
            type: 'Business type',
            noType: 'None (an organisation)',
            typeHint: 'The most specific schema.org type that is true, e.g. HomeAndConstructionBusiness for a cleaning or building company.',
            areaServed: 'Area served',
            areaServedHint: 'Places, comma-separated.',
            openingHours: 'Opening hours',
            openingHoursHint: 'One rule per line: days Mo Tu We Th Fr Sa Su, then the hours, e.g. Mo-Fr 08:00-17:00.',
            priceRange: 'Price range',
            foundingDate: 'Founded',
            latitude: 'Latitude',
            longitude: 'Longitude',
            coordinatesHint: 'In Google Maps: right-click the location and click the coordinates to copy them.',
            googleMaps: 'Google Maps listing',
            googleMapsHint: 'The full https:// link to the business on Google Maps.',
        },
        social: {
            title: 'Social profiles',
            intro: "Full URLs. These are published as the organisation's known profiles, so a wrong one tells search engines the site belongs to someone else.",
            searchIntro: 'Not linked from the website? Search Google for the name on:',
        },
    },
    whatsapp: {
        title: 'WhatsApp button',
        intro: 'The floating chat button, shown where the theme places it ({fn}).',
        show: 'Show the WhatsApp button',
        noNumber: 'No WhatsApp number yet, so the button stays hidden. Fill in the WhatsApp field under Contact & address, or the number below.',
        number: 'Number',
        numberHint: 'Blank = the WhatsApp number under Contact & address. Include the country code, e.g. +31.',
        openingMessage: 'Opening message ({locale})',
        openingMessageDefault: 'Opening message ({locale}, default)',
        openingMessageHint: 'Text the chat opens with; the visitor can still edit it.',
        label: 'Label ({locale})',
        labelHint: 'Next to the icon. Blank = icon only.',
        fallbackNote: "A language left blank uses the default language's text.",
        countries: 'Only show in these countries',
        countriesHint: "Empty = every visitor. With countries listed, visitors elsewhere (or whose country can't be told) don't see the button.",
        removeCountry: 'Remove {country}',
        addCountry: 'Add a country…',
    },
};
